import { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/Button';
import { getPartnersList } from "../../services/merchant";
import { AlertCircle } from 'lucide-react';
import Select from 'react-select';
import { MultiValue, ActionMeta } from "react-select";
import { getCategories, Category } from '../../services/caregory';
import { getSchemeBuckets, SchemeBucket } from '../../services/schemeBucket';


interface AddBrandFormProps {
  onSubmit: (BrandData: BrandSubmitData) => void;
  onCancel: () => void;
}


// Interface for the data structure passed to the parent component
interface BrandSubmitData {
  name: string;
  groupId: string;
  contactNo?: string;
  contactEmail?: string;
  isEv: boolean;
  isBrand?: boolean;
  isPublic: boolean;
  publicFor: string[];
  lender: string | null;
  category: string[];
  subCategory: string[];
  schemeBucket: string[];
  dashboard?: boolean;
  kycGstNo: string;
  kyc: {
    bank: {
      accountNo: string;
      holderName: string;
      ifsc: string;
    } | null;
    shop: {
      name: string;
      address: string;
      state: string;
      city: string;
      pin: string;
    } | null;
    docs: {
      shopFront: File | null;
      shopInner: File | null;
      evCer: File | null;
    } | null;
  } | null;
}

export interface BrandFormData {
  name: string;
  groupId: string;
  contactNo: string;
  contactEmail: string;
  isEv: boolean;
  isBrand?: boolean;
  isPublic: boolean;
  publicFor: string[];
  category: string[];
  subCategory: string[];
  schemeBucketId: string[];
  dashboard?: boolean;
  lender: string;
  kycGstNo: string;
  kycBank: {
    accountNo: string;
    holderName: string;
    ifsc: string;
  };
  kycShop: {
    name: string;
    address: string;
    state: string;
    city: string;
    pin: string;
  };
  kycDocs: {
    shopFront: File | null;
    shopInner: File | null;
    evCer: File | null;
  };
}

const AddBrandForm = ({ onSubmit, onCancel }: AddBrandFormProps) => {
  // Form state
  const [formData, setFormData] = useState<BrandFormData>({
    name: '',
    groupId: '',
    contactNo: '',
    contactEmail: '',
    isEv: false,
    isBrand: false,
    isPublic: false,
    publicFor: [],
    category: [],
    subCategory: [],
    schemeBucketId: [],
    dashboard: false,
    lender: '',
    kycGstNo: '',
    kycBank: {
      accountNo: '',
      holderName: '',
      ifsc: ''
    },
    kycShop: {
      name: '',
      address: '',
      state: '',
      city: '',
      pin: '',
    },
    kycDocs: {
      shopFront: null,
      shopInner: null,
      evCer: null
    }
  });

  // Active tab state
  const [activeTab, setActiveTab] = useState<'basic' | 'bank' | 'shop' | 'docs'>('basic');

  const [publicForOptions, setPublicForOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingPublicFor, setIsLoadingPublicFor] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [schemeBucketOptions, setSchemeBucketOptions] = useState<{ value: string; label: string }[]>([]);
  const [schemeBucketLoading, setSchemeBucketLoading] = useState(false);
  const [selectedSchemeBuckets, setSelectedSchemeBuckets] = useState<string[]>([]);


  // Subcategory options state
  const [subCategoryOptions, setSubCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [subCategoryLoading, setSubCategoryLoading] = useState(false);

  // Filter options based on search
  // const filteredOptions = categoryOptions.filter(option =>
  //   option.label.toLowerCase().includes(searchTerm.toLowerCase())
  // );

  const toggleCategory = (value: string) => {
    setFormData(prev => {
      const category = prev.category.includes(value)
        ? prev.category.filter(c => c !== value)
        : [...prev.category, value];
      return { ...prev, category };
    });
  };

  const handleCategoriesChange = (selected: any) => {
    const selectedValues = selected ? selected.map((opt: any) => opt.value) : [];

    // Check if "E2V" (Electric Vehicles) is included
    const isEvSelected = selectedValues.includes("E2V");

    setFormData(prev => ({
      ...prev,
      category: selectedValues,
      isEv: isEvSelected, // 👈 Automatically set EV flag
    }));

    if (errors.category) {
      setErrors(prev => ({ ...prev, category: undefined }));
    }
  };

  useEffect(() => {
    // If no categories are selected, clear subcategories
    if (!formData.category || formData.category.length === 0) {
      setSubCategoryOptions([]);
      return;
    }

    setSubCategoryLoading(true);

    // Join all selected category codes like: E2V,MOB
    const categoryQuery = formData.category.join(",");

    // 🔥 API call: /category?categoryId=E2V,MOB
    getCategories({ categoryId: categoryQuery })
      .then((result: any) => {
        const categories = Array.isArray(result) ? result : result?.data || [];
        const allSubs: string[] = [];

        categories.forEach((cat: any) => {
          if (Array.isArray(cat.subCategory)) {
            allSubs.push(...cat.subCategory);
          }
        });

        const uniqueSubs = [...new Set(allSubs)];
        const options = uniqueSubs.map(sub => ({ value: sub, label: sub }));

        setSubCategoryOptions(options);
        setFormData(prev => ({ ...prev, subCategory: [] })); // 👈 reset selected subs
      })

      .catch(() => setSubCategoryOptions([]))
      .finally(() => setSubCategoryLoading(false));
  }, [formData.category]);


  const handleSubCategoryChange = (selected: any) => {
    const selectedValues = selected ? selected.map((opt: any) => opt.value) : [];
    setFormData(prev => ({ ...prev, subCategory: selectedValues }));

    if (errors.subCategory) {
      setErrors(prev => ({ ...prev, subCategory: undefined }));
    }
  };


  useEffect(() => {
    setCategoryLoading(true);
    getCategories({ page: 1, pageSize: 100 })
      .then((result) => {
        const options = (result.data || []).map((cat: Category) => ({
          value: cat.code, // <-- use code, not _id
          label: `${cat.code} - ${cat.name}`,
        }));
        setCategoryOptions(options);
      })
      .catch(() => setCategoryOptions([]))
      .finally(() => setCategoryLoading(false));
  }, []);

  // Fetch scheme buckets
  useEffect(() => {
    const fetchSchemeBuckets = async () => {
      try {
        setSchemeBucketLoading(true);
        const result = await getSchemeBuckets({ page: 1, pageSize: 100 });
        const options = (result.data || []).map((bucket: any) => ({
          value: bucket.schemeBucketId, // This is the ID you want to submit
          label: bucket.name, // This is what user sees
        }));
        setSchemeBucketOptions(options);
      } catch (error) {
        setSchemeBucketOptions([]);
        console.error("Error fetching scheme bucket options:", error);
      } finally {
        setSchemeBucketLoading(false);
      }
    };

    fetchSchemeBuckets();
  }, []);

  // Update formData on select change
  const handleSchemeBucketChange = (selected: any) => {
    const selectedIds = selected ? selected.map((opt: any) => opt.value) : [];
    setFormData(prev => ({
      ...prev,
      schemeBucketId: selectedIds, // store only IDs
    }));

    // Clear errors
    if (errors.schemeBucketId) {
      setErrors(prev => ({ ...prev, schemeBucketId: undefined }));
    }
  };





  const reactSelectErrorStyles = {
    control: (base: any) => ({
      ...base,
      borderColor: '#f56565',
      boxShadow: '0 0 0 1px #f56565'
    })
  };



  const handlePublicForChange = (
    selectedOptions: MultiValue<{ value: string; label: string }> | null,
    actionMeta: ActionMeta<{ value: string; label: string }>
  ) => {
    const selected = selectedOptions ? selectedOptions.map(o => o.value) : [];
    setFormData(prev => ({ ...prev, publicFor: selected }));

    if (errors.publicFor) {
      setErrors(prev => ({ ...prev, publicFor: undefined }));
    }

    console.log("Updated publicFor:", selected); // debug
  };

  useEffect(() => {
    const fetchPublicFor = async () => {
      try {
        setIsLoadingPublicFor(true);
        const list = await getPartnersList(); // or your API to fetch "public for"
        const options = list.map(item => ({ value: item, label: item }));
        setPublicForOptions(options);
      } catch (error) {
        console.error('Error fetching Public For options:', error);
      } finally {
        setIsLoadingPublicFor(false);
      }
    };

    fetchPublicFor();
  }, []);



  // Files state for drag and drop
  const [files, setFiles] = useState<{
    shopFront: File | null,
    shopInner: File | null,
    evCer: File | null
  }>({
    shopFront: null,
    shopInner: null,
    evCer: null
  });

  // Lender options
  const lenderOptions = [
    { value: '', label: 'Select a lender' },
    { value: 'cashe', label: 'Cashe' },
    { value: 'zype', label: 'Zype' },
     { value: 'CAPRION', label: 'Caprion' }
  ];

  // Validation state
  const [errors, setErrors] = useState<Partial<Record<keyof BrandFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    // Handle nested fields (for KYC data)
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => {
        // Type-safe way to update nested fields
        if (section === 'kycBank') {
          return {
            ...prev,
            kycBank: {
              ...prev.kycBank,
              [field]: value
            }
          };
        } else if (section === 'kycShop') {
          return {
            ...prev,
            kycShop: {
              ...prev.kycShop,
              [field]: value
            }
          };
        } else if (section === 'kycDocs') {
          return {
            ...prev,
            kycDocs: {
              ...prev.kycDocs,
              [field]: value
            }
          };
        }
        return prev;
      });
    } else {
      // Handle top-level fields
      if (name === 'name') {
        // Convert to uppercase and remove spaces
        const formattedValue = value.toUpperCase().replace(/\s+/g, '');
        setFormData(prev => ({
          ...prev,
          [name]: formattedValue,
          // Auto-fill groupId with the same value
          groupId: formattedValue
        }));
      } else {
        // For checkbox inputs, use the checked property instead of value
        const newValue = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: newValue }));
      }
    }

    // Clear error when field is edited
    if (errors[name as keyof BrandFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };


  // Handle file drop
  const handleFileDrop = (fieldName: keyof typeof files, file: File | null) => {
    setFiles(prev => ({
      ...prev,
      [fieldName]: file
    }));

    setFormData(prev => ({
      ...prev,
      kycDocs: {
        ...prev.kycDocs,
        [fieldName]: file
      }
    }));
  };

  // Handle file removal
  const handleRemoveFile = (fieldName: keyof typeof files) => {
    setFiles(prev => ({
      ...prev,
      [fieldName]: null
    }));

    setFormData(prev => ({
      ...prev,
      kycDocs: {
        ...prev.kycDocs,
        [fieldName]: null
      }
    }));
  };

  // Validate form
  const validateForm = (): boolean => {
    console.log("Running validation", activeTab);
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Brand name is required';
    }

    // Contact number validation
    // Contact number validation (ONLY if present)
    if (formData.contactNo.trim() && !/^\d{10}$/.test(formData.contactNo)) {
      newErrors.contactNo = 'Please enter a valid 10-digit mobile number';
    }

    // Email validation (ONLY if present)
    if (
      formData.contactEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
    ) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }


    // IFSC code validation (if provided)
    if (formData.kycBank.ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.kycBank.ifsc)) {
      newErrors['kycBank.ifsc'] = 'Please enter a valid IFSC code';
    }

    // pin validation (if provided)
    if (formData.kycShop.pin && !/^\d{6}$/.test(formData.kycShop.pin)) {
      newErrors['kycShop.pin'] = 'Please enter a valid 6-digit pin';
    }

    console.log("Validation errors:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to check if there's an error for a field
  const hasError = (fieldName: string): boolean => {
    return !!errors[fieldName as keyof BrandFormData];
  };

  // Helper function to get error message for a field
  const getErrorMessage = (fieldName: string): string | undefined => {
    return errors[fieldName as keyof BrandFormData];
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    console.log("Form submit triggered", activeTab);
    e.preventDefault();

    if (!validateForm()) {
      console.log("Validation failed");
      return;
    }

    setIsSubmitting(true);
    console.log("Form is valid, proceeding with submission");

    try {
      // Prepare KYC data, filtering out empty values
      const kycData = {
        bank: Object.values(formData.kycBank).some(val => val.trim() !== '') ? formData.kycBank : null,
        shop: Object.values(formData.kycShop).some(val => val.trim() !== '') ? formData.kycShop : null,
        docs: files.shopFront || files.shopInner || files.evCer ? {
          shopFront: files.shopFront,
          shopInner: files.shopInner,
          evCer: formData.isEv ? files.evCer : null
        } : null
      };

      const cleanedContactNo =
        formData.contactNo?.trim() ? formData.contactNo.trim() : undefined;

      const cleanedContactEmail =
        formData.contactEmail?.trim() ? formData.contactEmail.trim() : undefined;


      // Create Brand payload
      const BrandPayload: BrandSubmitData = {
        name: formData.name,
        groupId: formData.groupId,
        ...(cleanedContactNo && { contactNo: cleanedContactNo }),
        ...(cleanedContactEmail && { contactEmail: cleanedContactEmail }),
        isEv: formData.isEv,
        isBrand: formData.isBrand,
        isPublic: formData.isPublic,
        publicFor: formData.publicFor,
        category: formData.category,
        subCategory: formData.subCategory,
        schemeBucket: formData.schemeBucketId,
        dashboard: formData.dashboard,
        lender: formData.lender ? formData.lender : null,
        kycGstNo: formData.kycGstNo,
        kyc: kycData.bank || kycData.shop || kycData.docs ? {
          bank: kycData.bank,
          shop: kycData.shop,
          docs: kycData.docs
        } : null
      };

      console.log("Submitting Brand payload:", BrandPayload);
      // In a real app, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 800));
      onSubmit(BrandPayload);
    } catch (error) {
      console.error('Error creating Brand:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            type="button"
            className={`mr-2 py-2 px-4 text-sm font-medium border-b-2 ${activeTab === 'basic'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveTab('basic')}
          >
            Basic Details
          </button>
          <button
            type="button"
            className={`mr-2 py-2 px-4 text-sm font-medium border-b-2 ${activeTab === 'bank'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveTab('bank')}
          >
            Bank Details
          </button>
          <button
            type="button"
            className={`mr-2 py-2 px-4 text-sm font-medium border-b-2 ${activeTab === 'shop'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveTab('shop')}
          >
            Shop Details
          </button>
          <button
            type="button"
            className={`py-2 px-4 text-sm font-medium border-b-2 ${activeTab === 'docs'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveTab('docs')}
          >
            Documents
          </button>
        </nav>
      </div>

      {/* Basic Details Tab */}
      {activeTab === 'basic' && (
        <div className="space-y-4 mb-6">
          {/* Brand Name field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Partner/Org Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${hasError('name') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="ENTER Partner/ORG NAME (NO SPACES)"
            />
            {hasError('name') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('name')}</p>
            )}
          </div>

          {/* Group ID field */}
          <div>
            <label htmlFor="groupId" className="block text-sm font-medium text-gray-700 mb-1">
              Group ID <span className="text-red-500">*</span>
            </label>
            <input
              id="groupId"
              name="groupId"
              type="text"
              value={formData.groupId}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed"
              placeholder="Auto-filled from Partner/Org Name"
            />
            <p className="mt-1 text-xs text-gray-500">This field is automatically filled based on the Partner/Org Name.</p>
          </div>

          {/* Contact Number field */}
          <div>
            <label htmlFor="contactNo" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Number
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span className="text-gray-500">+91</span>
              </div>
              <input
                id="contactNo"
                name="contactNo"
                type="tel"
                value={formData.contactNo}
                onChange={handleChange}
                className={`w-full pl-12 pr-3 py-2 border ${hasError('contactNo') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Enter 10-digit contact number"
                maxLength={10}
              />
            </div>
            {hasError('contactNo') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('contactNo')}</p>
            )}
          </div>

          {/* Contact Email field */}
          <div>
            <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Email
            </label>
            <input
              id="contactEmail"
              name="contactEmail"
              type="email"
              value={formData.contactEmail}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${hasError('contactEmail') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter contact email"
            />
            {hasError('contactEmail') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('contactEmail')}</p>
            )}
          </div>

          {/* Lender field */}
          <div>
            <label htmlFor="lender" className="block text-sm font-medium text-gray-700 mb-1">
              Lender <span className="text-red-500">*</span>
            </label>
            <Select
              id="lender"
              options={lenderOptions}
              value={
                lenderOptions.length > 0
                  ? lenderOptions.find(option => option.value === formData.lender)
                  : null
              }
              onChange={(selectedOption) =>
                setFormData(prev => ({
                  ...prev,
                  lender: selectedOption ? selectedOption.value : "",
                }))
              }
              className={errors.lender ? "react-select-error" : ""}
              classNamePrefix="react-select"
              placeholder="Select Lender"
              noOptionsMessage={() => "No lenders found"}
              styles={errors.lender ? reactSelectErrorStyles : undefined}
            />
            {errors.lender && (
              <p className="ml-2 block text-sm text-red-500 flex items-center">
                <AlertCircle size={12} className="mr-1" />
                {errors.lender}
              </p>
            )}
          </div>


          {/* EV Brand field */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand Type
            </label>
            <div className="flex items-center mt-1">
              <input
                id="isEv"
                name="isEv"
                type="checkbox"
                checked={formData.isEv}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isEv" className="ml-2 block text-sm text-gray-700">
                Electric Vehicle (EV) Brand
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">Check this box if this Brand is for electric vehicles</p>
          </div> */}

          {/* Categories field */}
          <div>
            <label htmlFor="categories" className="block text-sm font-medium text-gray-700 mb-1">
              Categories <span className="text-red-500">*</span>
            </label>
            <Select
              id="categories"
              isMulti
              options={categoryOptions}
              value={categoryOptions.filter(option => formData.category?.includes(option.value))}
              onChange={handleCategoriesChange}
              className={errors.category ? 'react-select-error' : ''}
              classNamePrefix="react-select"
              placeholder="Select Categories"
              isLoading={categoryLoading}
              isDisabled={categoryLoading}
              noOptionsMessage={() => "No categories found"}
              styles={errors.category ? reactSelectErrorStyles : undefined}
            />
            {errors.category && (
              <p className="flex items-center mt-1 text-xs text-red-500">
                <AlertCircle size={12} className="mr-1" />
                {errors.category}
              </p>
            )}
            {/* Chips for selected codes */}
            {formData.category.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.category.map(code => (
                  <span key={code} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                    {code}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Subcategories field (only show if available) */}
          {subCategoryOptions.length > 0 && (
            <div className="mt-4">
              <label htmlFor="subCategories" className="block text-sm font-medium text-gray-700 mb-1">
                Subcategories <span className="text-red-500">*</span>
              </label>
              <Select
                id="subCategories"
                isMulti
                options={subCategoryOptions}
                value={subCategoryOptions.filter(opt =>
                  formData.subCategory?.includes(opt.value)
                )}
                onChange={handleSubCategoryChange}
                className={errors.subCategory ? 'react-select-error' : ''}
                classNamePrefix="react-select"
                placeholder="Select Subcategories"
                isLoading={subCategoryLoading}
                isDisabled={subCategoryLoading}
                noOptionsMessage={() => "No subcategories found"}
                styles={errors.subCategory ? reactSelectErrorStyles : undefined}
              />
              {errors.subCategory && (
                <p className="flex items-center mt-1 text-xs text-red-500">
                  <AlertCircle size={12} className="mr-1" />
                  {errors.subCategory}
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="schemeBuckets" className="block text-sm font-medium text-gray-700 mb-1">
              Scheme Buckets
            </label>
            <Select
              id="schemeBuckets"
              isMulti
              options={schemeBucketOptions}
              value={schemeBucketOptions.filter(option =>
                formData.schemeBucketId?.includes(option.value)
              )}
              onChange={handleSchemeBucketChange} // Use only this handler
              className={errors.schemeBucketId ? 'react-select-error' : ''}
              classNamePrefix="react-select"
              placeholder="Select Scheme Buckets"
              isLoading={schemeBucketLoading}
              isDisabled={schemeBucketLoading}
              noOptionsMessage={() => "No schemeBuckets found"}
              styles={errors.schemeBucketId ? reactSelectErrorStyles : undefined}
            />


            {errors.schemeBucketId && (
              <p className="flex items-center mt-1 text-xs text-red-500">
                <AlertCircle size={12} className="mr-1" />
                {errors.schemeBucketId}
              </p>
            )}

            {/* Chips for selected names */}
            {formData.schemeBucketId?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.schemeBucketId.map(id => {
                  const bucket = schemeBucketOptions.find(opt => opt.value === id);
                  return (
                    <span key={id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {bucket?.label || id} {/* show name */}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center mt-2">
            <input
              id="isBrand"
              name="isBrand"
              type="checkbox"
              checked={formData.isBrand}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  isBrand: e.target.checked,
                }))
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isBrand" className="ml-2 block text-sm text-gray-700">
              Is Brand
            </label>
          </div>

          {/* Show these ONLY if isBrand is true */}
          {formData.isBrand && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Public Brand
                </label>
                <div className="flex items-center mt-1">
                  <input
                    id="isPublic"
                    name="isPublic"
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                    Brand is Public
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">Check this box if this Brand is Public</p>
              </div>

              <div>
                <label htmlFor="publicFor" className="block text-sm font-medium text-gray-700 mb-1">
                  Public For
                </label>
                <Select
                  id="publicFor"
                  isMulti
                  options={publicForOptions}
                  value={
                    publicForOptions.length > 0
                      ? publicForOptions.filter(option =>
                        formData.publicFor?.includes(option.value)
                      )
                      : []
                  }
                  onChange={handlePublicForChange}
                  className={errors.publicFor ? "react-select-error" : ""}
                  classNamePrefix="react-select"
                  placeholder="Select Public For"
                  isLoading={isLoadingPublicFor}
                  isDisabled={isLoadingPublicFor}
                  noOptionsMessage={() => "No partners found"}
                />
                {errors.publicFor && (
                  <p className="ml-2 block text-sm text-gray-700 flex items-center">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.publicFor}
                  </p>
                )}
              </div>
            </>
          )}


          <div className="flex items-center mt-2">
            <input
              id="dashboard"
              name="dashboard"
              type="checkbox"
              checked={formData.dashboard}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  dashboard: e.target.checked,
                }))
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="dashboard" className="ml-2 block text-sm text-gray-700">
              Partner Dashboard Access
            </label>
          </div>


        </div>
      )}

      {/* Bank Details Tab */}
      {activeTab === 'bank' && (
        <div className="space-y-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-700">These KYC bank details are optional. You can add them now or later.</p>
          </div>

          {/* Account Number field */}
          <div>
            <label htmlFor="kycBank.accountNo" className="block text-sm font-medium text-gray-700 mb-1">
              Account Number
            </label>
            <input
              id="kycBank.accountNo"
              name="kycBank.accountNo"
              type="text"
              value={formData.kycBank.accountNo}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${hasError('kycBank.accountNo') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter bank account number"
            />
            {hasError('kycBank.accountNo') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycBank.accountNo')}</p>
            )}
          </div>

          {/* Account Holder Name */}
          <div>
            <label htmlFor="kycBank.holderName" className="block text-sm font-medium text-gray-700 mb-1">
              Account Holder Name
            </label>
            <input
              id="kycBank.holderName"
              name="kycBank.holderName"
              type="text"
              value={formData.kycBank.holderName}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${hasError('kycBank.holderName') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter account holder name"
            />
            {hasError('kycBank.holderName') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycBank.holderName')}</p>
            )}
          </div>

          {/* IFSC Code */}
          <div>
            <label htmlFor="kycBank.ifsc" className="block text-sm font-medium text-gray-700 mb-1">
              IFSC Code
            </label>
            <input
              id="kycBank.ifsc"
              name="kycBank.ifsc"
              type="text"
              value={formData.kycBank.ifsc}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${hasError('kycBank.ifsc') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter IFSC code"
            />
            {hasError('kycBank.ifsc') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycBank.ifsc')}</p>
            )}
          </div>
        </div>
      )}

      {/* Shop Details Tab */}
      {activeTab === 'shop' && (
        <div className="space-y-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-700">These KYC shop details are optional. You can add them now or later.</p>
          </div>

          {/* GST Number */}
          <div>
            <label htmlFor="kycGstNo" className="block text-sm font-medium text-gray-700 mb-1">
              GST Number
            </label>
            <input
              id="kycGstNo"
              name="kycGstNo"
              type="text"
              value={formData.kycGstNo || ''}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${hasError('kycGstNo') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter GST number"
            />
            {hasError('kycGstNo') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycGstNo')}</p>
            )}
          </div>

          {/* Shop Name */}
          <div>
            <label htmlFor="kycShop.name" className="block text-sm font-medium text-gray-700 mb-1">
              Shop Name
            </label>
            <input
              id="kycShop.name"
              name="kycShop.name"
              type="text"
              value={formData.kycShop.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${hasError('kycShop.name') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter shop name"
            />
            {hasError('kycShop.name') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycShop.name')}</p>
            )}
          </div>

          {/* Shop Address */}
          <div>
            <label htmlFor="kycShop.address" className="block text-sm font-medium text-gray-700 mb-1">
              Shop Address
            </label>
            <textarea
              id="kycShop.address"
              name="kycShop.address"
              value={formData.kycShop.address}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${hasError('kycShop.address') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter shop address"
              rows={3}
            />
            {hasError('kycShop.address') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycShop.address')}</p>
            )}
          </div>

          {/* State, City and pin in a grid */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="kycShop.state" className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                id="kycShop.state"
                name="kycShop.state"
                type="text"
                value={formData.kycShop.state}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${hasError('kycShop.state') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="State"
              />
              {hasError('kycShop.state') && (
                <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycShop.state')}</p>
              )}
            </div>

            <div>
              <label htmlFor="kycShop.city" className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                id="kycShop.city"
                name="kycShop.city"
                type="text"
                value={formData.kycShop.city}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${hasError('kycShop.city') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="City"
              />
              {hasError('kycShop.city') && (
                <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycShop.city')}</p>
              )}
            </div>

            <div>
              <label htmlFor="kycShop.pin" className="block text-sm font-medium text-gray-700 mb-1">
                PinCode
              </label>
              <input
                id="kycShop.pin"
                name="kycShop.pin"
                type="text"
                value={formData.kycShop.pin}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${hasError('kycShop.pin') ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="PinCode"
                maxLength={6}
              />
              {hasError('kycShop.pin') && (
                <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycShop.pin')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'docs' && (
        <div className="space-y-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-700">KYC document uploads are optional. You can add them now or later.</p>
          </div>

          {/* Shop Front Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shop Front Image
            </label>
            <div className={`border-2 border-dashed ${hasError('kycDocs.shopFront') ? 'border-red-500' : 'border-gray-300'} rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition`}>
              <input
                type="file"
                id="shopFront"
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleFileDrop('shopFront', e.target.files[0]);
                  }
                }}
              />
              <label htmlFor="shopFront" className="cursor-pointer">
                {files.shopFront ? (
                  <div className="flex items-center justify-center">
                    <span className="text-sm text-gray-500">Selected: {files.shopFront.name}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mt-1 text-sm text-gray-500">Drag and drop or click to upload</p>
                  </div>
                )}
              </label>
            </div>
            {hasError('kycDocs.shopFront') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycDocs.shopFront')}</p>
            )}

            {files.shopFront && (
              <div className="mt-2 relative">
                <div className="flex items-center p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-grow">
                    <p className="text-sm font-medium text-blue-700 truncate">
                      {files.shopFront.name}
                    </p>
                    <p className="text-xs text-blue-500">
                      {(files.shopFront.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile('shopFront')}
                    className="p-1 bg-red-100 rounded-full text-red-500 hover:bg-red-200"
                    title="Remove file"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Shop Inner Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shop Inner Image
            </label>
            <div className={`border-2 border-dashed ${hasError('kycDocs.shopInner') ? 'border-red-500' : 'border-gray-300'} rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition`}>
              <input
                type="file"
                id="shopInner"
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleFileDrop('shopInner', e.target.files[0]);
                  }
                }}
              />
              <label htmlFor="shopInner" className="cursor-pointer">
                {files.shopInner ? (
                  <div className="flex items-center justify-center">
                    <span className="text-sm text-gray-500">Selected: {files.shopInner.name}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mt-1 text-sm text-gray-500">Drag and drop or click to upload</p>
                  </div>
                )}
              </label>
            </div>
            {hasError('kycDocs.shopInner') && (
              <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycDocs.shopInner')}</p>
            )}

            {files.shopInner && (
              <div className="mt-2 relative">
                <div className="flex items-center p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-grow">
                    <p className="text-sm font-medium text-blue-700 truncate">
                      {files.shopInner.name}
                    </p>
                    <p className="text-xs text-blue-500">
                      {(files.shopInner.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile('shopInner')}
                    className="p-1 bg-red-100 rounded-full text-red-500 hover:bg-red-200"
                    title="Remove file"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* EV Certificate (only show if isEv is true) */}
          {formData.isEv && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                EV Certificate
              </label>
              <div className={`border-2 border-dashed ${hasError('kycDocs.evCer') ? 'border-red-500' : 'border-gray-300'} rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition`}>
                <input
                  type="file"
                  id="evCer"
                  className="hidden"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleFileDrop('evCer', e.target.files[0]);
                    }
                  }}
                />
                <label htmlFor="evCer" className="cursor-pointer">
                  {files.evCer ? (
                    <div className="flex items-center justify-center">
                      <span className="text-sm text-gray-500">Selected: {files.evCer.name}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="mt-1 text-sm text-gray-500">Drag and drop or click to upload</p>
                    </div>
                  )}
                </label>
              </div>
              {hasError('kycDocs.evCer') && (
                <p className="mt-1 text-xs text-red-500">{getErrorMessage('kycDocs.evCer')}</p>
              )}

              {formData.isEv && files.evCer && (
                <div className="mt-2 relative">
                  <div className="flex items-center p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex-grow">
                      <p className="text-sm font-medium text-blue-700 truncate">
                        {files.evCer.name}
                      </p>
                      <p className="text-xs text-blue-500">
                        {(files.evCer.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('evCer')}
                      className="p-1 bg-red-100 rounded-full text-red-500 hover:bg-red-200"
                      title="Remove file"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Form actions - Move these buttons outside of the tab content */}
      <div className="flex justify-between items-center space-x-3 pt-4 border-t border-gray-200">
        <div className="flex space-x-2">
          {activeTab !== 'basic' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (activeTab === 'bank') setActiveTab('basic');
                else if (activeTab === 'shop') setActiveTab('bank');
                else if (activeTab === 'docs') setActiveTab('shop');
              }}
            >
              Previous
            </Button>
          )}

          {activeTab !== 'docs' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (activeTab === 'basic') setActiveTab('bank');
                else if (activeTab === 'bank') setActiveTab('shop');
                else if (activeTab === 'shop') setActiveTab('docs');
              }}
            >
              Next
            </Button>
          )}
        </div>

        <div className="flex space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            onClick={() => {
              console.log("Submit button clicked manually");
              // This click handler is a backup in case form submission doesn't work
            }}
          >
            {isSubmitting ? 'Adding...' : 'Add Partner'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AddBrandForm; 